import React, { useState, useEffect, useRef } from "react";
import { LocalPagamentoDto } from "../../../shared/dtos/LocalPagamento.dto";
import PagamentoFormModal from "../../../components/PagamentoFormModal/pagamentoFormModal";
import localPagamentoService from "../../../services/LocalPagamentoService";
import Swal from "sweetalert2";

import {
  ButtonAction,
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableContainer,
} from "../../../components/Table/styles";
import { IoIosAddCircleOutline, IoMdClose } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import { Colors } from "../../../shared/colors/colors";
import Button from "../../../components/Button/button";
import { MdOutlineViewColumn } from "react-icons/md";
import { Dropdown } from "react-bootstrap";
import {
  DropdownDivider,
  DropdownScroll,
} from "../../../components/Dropdowns/styles";
import {
  GrayTitle,
  StyledHR,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import { ContainerButtons, MainContainer } from "./localPagamento.styles";
import { useAuth } from "../../../AuthContext/AuthContext";
import { ModulosEnum } from "../../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../../shared/enum/permicao.enum";

const formatarDiaVencimento = (
  dataVencimento: string | undefined | null | number
): string => {
  if (!dataVencimento) return "-";

  // Convert to string if it's not already
  const dataString = String(dataVencimento);

  // Se a data já vier no formato YYYY-MM-DD
  if (dataString.includes("-")) {
    const [ano, mes, diaMes] = dataString.split("-");
    return `${diaMes}/${mes}/${ano}`;
  }

  // Se for apenas o dia do mês
  return dataString.padStart(2, "0");
};

const formatarCNPJ = (cnpj: string | undefined): string => {
  if (!cnpj) return "-";

  // Remove caracteres não numéricos
  const numeros = cnpj.replace(/\D/g, "");

  // Adiciona zeros à esquerda se necessário
  const cnpjCompleto = numeros.padStart(14, "0");

  // Aplica a máscara XX.XXX.XXX/XXXX-XX
  return cnpjCompleto.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

const LocalPagamento: React.FC = () => {
  const { checkPermission } = useAuth();
  const editOnly = checkPermission(
    ModulosEnum.LOCAL_PAGAMENTO,
    PermissoesEnum.ESCRITA
  );

  const [localPagamento, setLocalPagamento] = useState<LocalPagamentoDto[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoacao, setSelectedDoacao] =
    useState<LocalPagamentoDto | null>(null);
  const [isOpen, setIsOpen] = useState<number | null>(null);
  const [isOpenMenuCollumn, setIsOpenMenuCollumn] = useState(false);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const defaultColumns = [
    "Descrição",
    "CNPJ",
    "Convênio",
    "Carteira",
    "Banco",
    "Posto",
    "Agência",
    "Conta Corrente",
    "Dia de Vencimento",
    "Nosso Número",
  ];

  const loadVisibleColumns = () => {
    try {
      const savedColumns = sessionStorage.getItem("localPagamentoVisibleColumns");
      if (!savedColumns) return defaultColumns;

      const parsedColumns = JSON.parse(savedColumns);
      return ensureDefaultColumns(parsedColumns);
    } catch (error) {
      console.error("Erro ao carregar colunas:", error);
      return defaultColumns;
    }
  };

  // Função para garantir que sempre tenhamos pelo menos as colunas padrão
  const ensureDefaultColumns = (columns: string[]) => {
    return columns.length > 0 ? columns : defaultColumns;
  };

  const [localPagamentoVisibleColumns, setlocalPagamentoVisibleColumns] =
    useState<string[]>(loadVisibleColumns());
  const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>(
    loadVisibleColumns()
  );

  // Função para aplicar as mudanças
  const handleApplyColumns = () => {
    const columnsToSave = ensureDefaultColumns(tempVisibleColumns);
    setlocalPagamentoVisibleColumns(columnsToSave);
    sessionStorage.setItem(
      "localPagamentoVisibleColumns",
      JSON.stringify(columnsToSave)
    );
    setIsOpenMenuCollumn(false);
  };

  const toggleDropdown = (id: number) => {
    setIsOpen(id === isOpen ? null : id);
  };

  const handleAddDoacao = (doacao: LocalPagamentoDto) => {
    setLocalPagamento([...localPagamento, { ...doacao, id: Date.now() }]);
    setShowModal(false);
    getDoacoes();
  };

  const handleEditDoacao = (doacao: LocalPagamentoDto) => {
    setLocalPagamento(
      localPagamento.map((d) => (d.id === doacao.id ? doacao : d))
    );
    setShowModal(false);
    getDoacoes();
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: "Tem certeza?",
        text: "Você realmente deseja excluir este pagamento? Esta ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        await localPagamentoService.deleteLocalPagamento(id);
        setLocalPagamento((prev) => prev.filter((doacao) => doacao.id !== id));
        Swal.fire("Sucesso", "Item excluído com sucesso!", "success");
      }
    } catch (error) {
      console.error(`[handleDelete] Erro ao excluir ID ${id}:`, error);
      Swal.fire(
        "Erro",
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao excluir item.",
        "error"
      );
    }
  };

  // Função para alternar colunas no estado temporário
  const handleColumnToggle = (column: string) => {
    // Se a coluna é padrão, não permite desmarcar
    if (defaultColumns.includes(column)) {
      return;
    }

    setTempVisibleColumns((prev: string[]) => {
      return prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column];
    });
  };

  const getDoacoes = async () => {
    const response = await localPagamentoService.getAllLocaisPagamento();
    setLocalPagamento(response);
  };

  useEffect(() => {
    getDoacoes();
  }, []);

  useEffect(() => {
    if (isOpenMenuCollumn) {
      setTempVisibleColumns(localPagamentoVisibleColumns); // Inicializa o estado temporário com as colunas visíveis atuais
    }
  }, [isOpenMenuCollumn, localPagamentoVisibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpenMenuCollumn(false);
      }
    };

    if (isOpenMenuCollumn) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenMenuCollumn]);
  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  return (
    <MainContainer>
      <TitleAndButton>
        <GrayTitle>Local de Pagamento</GrayTitle>
        <ContainerButtons ref={wrapperRef}>
          <Button
            schemaButton="out-primary"
            action={() => setIsOpenMenuCollumn(!isOpenMenuCollumn)}
            icon={
              <MdOutlineViewColumn size={20} style={{ marginRight: "8px" }} />
            }
          >
            Colunas
          </Button>

          <Dropdown.Menu
            ref={dropdownMenuRef}
            show={isOpenMenuCollumn}
            style={{ marginTop: "50px" }}
          >
            <DropdownScroll>
              {[
                "Descrição",
                "CNPJ",
                "Convênio",
                "Carteira",
                "Banco",
                "Posto",
                "Coopertaiva",
                "Código de beneficiário",
                "Agência",
                "Conta Corrente",
                "Dia de Vencimento",
                "Byte Geração",
                "Nosso Número",
                "Código de barras",
                "Layout Remessa",
                "Layout Retorno",
                "Chave Pix",
                "Imprimir Docs",
                "Gerar Remessa",
                "Instrução Boleto 1",
                "Instrução Boleto 2",
                "Instrução Boleto 3",
                "Instrução Boleto 4",
              ].map((column, index, array) => (
                <React.Fragment key={column}>
                  {!defaultColumns.includes(column) && (
                    <Dropdown.Item
                      eventKey="1"
                      onClick={() => handleColumnToggle(column)}
                      style={{ color: Colors.primary }}
                    >
                      <img
                        src={
                          tempVisibleColumns.includes(column)
                            ? "/images/checkbox-marked.svg"
                            : "/images/checkbox-blank-outline.svg"
                        }
                        alt={
                          tempVisibleColumns.includes(column)
                            ? defaultColumns.includes(column)
                              ? "Bloqueado"
                              : "Selecionado"
                            : "Não selecionado"
                        }
                        className="checkbox-image"
                      />
                      <label>{column}</label>
                      {array
                        .filter((col) => !defaultColumns.includes(col))
                        .indexOf(column) <
                        array.filter((col) => !defaultColumns.includes(col))
                          .length -
                          1 && <DropdownDivider />}
                    </Dropdown.Item>
                  )}
                </React.Fragment>
              ))}
            </DropdownScroll>
            {JSON.stringify(tempVisibleColumns.sort()) !==
              JSON.stringify(localPagamentoVisibleColumns.sort()) && (
              <Dropdown.Item>
                <MenuTableDivider />
                <Button schemaButton="with-margin" action={handleApplyColumns}>
                  Aplicar
                </Button>
              </Dropdown.Item>
            )}
          </Dropdown.Menu>

          {editOnly && (
            <Button
              schemaButton="full-primary"
              action={() => {
                setSelectedDoacao(null);
                setShowModal(true);
              }}
              icon={
                <IoIosAddCircleOutline
                  size={20}
                  style={{ marginRight: "8px" }}
                />
              }
            >
              Adicionar Pagamento
            </Button>
          )}
        </ContainerButtons>
      </TitleAndButton>
      <StyledHR />
      <TableContainer>
        <Table id="users-table">
          <TableHead>
            <TableRow>
              {localPagamentoVisibleColumns.includes("Descrição") && (
                <TableHeader>Descrição</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("CNPJ") && (
                <TableHeader>CNPJ</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Convênio") && (
                <TableHeader>Convênio</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Carteira") && (
                <TableHeader>Carteira</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Banco") && (
                <TableHeader>Banco</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Posto") && (
                <TableHeader>Posto</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Cooperativa") && (
                <TableHeader>Cód. Cooperativa</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes(
                "Código de beneficiário"
              ) && <TableHeader>Cod. de beneficiário</TableHeader>}

              {localPagamentoVisibleColumns.includes("Agência") && (
                <TableHeader>Agência</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Conta Corrente") && (
                <TableHeader>Conta Corrente</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Dia de Vencimento") && (
                <TableHeader>Dia Vencimento</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Byte Geração") && (
                <TableHeader>Byte Geração</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Nosso Número") && (
                <TableHeader>Nosso Número</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Código de Barras") && (
                <TableHeader>Tipo Código de Barras</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Layout Remessa") && (
                <TableHeader>Layout Remessa</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Layout Retorno") && (
                <TableHeader>Layout Retorno</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Chave Pix") && (
                <TableHeader>Chave Pix</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Imprimir Docs") && (
                <TableHeader>Imprimir Docs</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Gerar Remessa") && (
                <TableHeader>Gerar Remessa</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Instrução Boleto 1") && (
                <TableHeader>Instrução Boleto 1</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Instrução Boleto 2") && (
                <TableHeader>Instrução Boleto 2</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Instrução Boleto 3") && (
                <TableHeader>Instrução Boleto 3</TableHeader>
              )}
              {localPagamentoVisibleColumns.includes("Instrução Boleto 4") && (
                <TableHeader>Instrução Boleto 4</TableHeader>
              )}
              {editOnly && <TableHeader>Ações</TableHeader>}
            </TableRow>
          </TableHead>
          <tbody>
            {localPagamento.length > 0 ? (
              localPagamento.map((local) => (
                <TableRow key={local.id}>
                  {localPagamentoVisibleColumns.includes("Descrição") && (
                    <TableData>{local.descricao}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("CNPJ") && (
                    <TableData>{formatarCNPJ(local.cnpj)}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Convênio") && (
                    <TableData>{local.convenio}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Carteira") && (
                    <TableData>{local.carteira}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Banco") && (
                    <TableData>{local.banco}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Posto") && (
                    <TableData>{local.posto}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Cooperativa") && (
                    <TableData>{local.cooperativa}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes(
                    "Código de beneficiário"
                  ) && <TableData>{local.codBeneficiario}</TableData>}
                  {localPagamentoVisibleColumns.includes("Agência") && (
                    <TableData>{local.agencia}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Conta Corrente") && (
                    <TableData>{local.contaCorrente}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes(
                    "Dia de Vencimento"
                  ) && (
                    <TableData>
                      {formatarDiaVencimento(local.diaVencimento)}
                    </TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Byte Geração") && (
                    <TableData>{local.byteGeracao}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Nosso Número") && (
                    <TableData>{local.nossoNumero}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes(
                    "Código de Barras"
                  ) && <TableData>{local.tipoCodigoBarras}</TableData>}
                  {localPagamentoVisibleColumns.includes("Layout Remessa") && (
                    <TableData>{local.layoutRemessa?.nome || ""}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Layout Retorno") && (
                    <TableData>{local.layoutRetorno?.nome || ""}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Chave Pix") && (
                    <TableData>{local.chavePix}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Imprimir Docs") && (
                    <TableData>{local.imprimirDocs ? "Sim" : "Não"}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes("Gerar Remessa") && (
                    <TableData>{local.gerarRemessa ? "Sim" : "Não"}</TableData>
                  )}
                  {localPagamentoVisibleColumns.includes(
                    "Instrução Boleto 1"
                  ) && <TableData>{local.instrucaoBoleto1}</TableData>}
                  {localPagamentoVisibleColumns.includes(
                    "Instrução Boleto 2"
                  ) && <TableData>{local.instrucaoBoleto2}</TableData>}
                  {localPagamentoVisibleColumns.includes(
                    "Instrução Boleto 3"
                  ) && <TableData>{local.instrucaoBoleto3}</TableData>}
                  {localPagamentoVisibleColumns.includes(
                    "Instrução Boleto 4"
                  ) && <TableData>{local.instrucaoBoleto4}</TableData>}
                  {editOnly && (
                    <TableData
                      key={`actions-${local.id}`}
                      style={{
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <ButtonAction onClick={() => toggleDropdown(local.id!)}>
                        {isOpen === local.id ? (
                          <IoMdClose
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        ) : (
                          <SlOptionsVertical
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        )}
                      </ButtonAction>
                      <MenuTable
                        isLastRows={
                          localPagamento.length > 0 &&
                          localPagamento.indexOf(local) >=
                            localPagamento.length -
                              Math.ceil(localPagamento.length / 2)
                        }
                        ref={(el: HTMLDivElement | null) => {
                          // Armazena a referência no mapa usando o ID do registro como chave
                          menuRefsMap.current.set(local.id!, el);
                        }}
                        show={isOpen === local.id}
                        align="start"
                      >
                        <MenuTableItem
                          eventKey="1"
                          onClick={() => {
                            setSelectedDoacao(local);
                            setShowModal(true);
                            setIsOpen(null);
                          }}
                        >
                          Editar
                        </MenuTableItem>
                        <MenuTableDivider />
                        <MenuTableItem
                          eventKey="2"
                          onClick={() => {
                            handleDelete(local.id!);
                            setIsOpen(null);
                          }}
                        >
                          Excluir
                        </MenuTableItem>
                        {/* <MenuTableDivider />
                      <MenuTableItem eventKey="1" onClick={() => {}}>
                        Gerar Remessas
                      </MenuTableItem>
                      <MenuTableDivider />
                      <MenuTableItem eventKey="2" onClick={() => {}}>
                        Imprimir Doc
                      </MenuTableItem> */}
                      </MenuTable>
                    </TableData>
                  )}
                </TableRow>
              ))
            ) : (
              <tr>
                <TableData
                  colSpan={localPagamentoVisibleColumns.length + 1}
                  className="text-center"
                >
                  Nenhum usuário encontrado.
                </TableData>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <PagamentoFormModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedDoacao(null);
        }}
        onSavePagamento={selectedDoacao ? handleEditDoacao : handleAddDoacao}
        pagamentoParaEditar={selectedDoacao}
      />
    </MainContainer>
  );
};

export default LocalPagamento;
